import React, { useEffect, useMemo } from "react";
import { Controller, useWatch } from "react-hook-form";
import ErrorMessage from "../../../components/comman/ErrorMessage";
import DeleteIcon from "../../../components/Icons/DeleteIcon";
import { formatAmount } from "../../../utility/CommonFunctions";
import { useLocation } from "react-router-dom";

const BudgetRequestItemRow = ({
    control,
    register,
    sectionIndex,
    headIndex,
    itemIndex,
    remove,
    itemsList,
    handleItemChange,
    errors,
    usedItems,
    itemLabel = "Item",
    isStaffBudget = false,
    isServiceRequest = false,
    setValue,
    isEditMode,
    isOldData = false,
    activeItem = {
        rate: 0,
        gst: 0,
        month: 0
    }
}) => {
    const location = useLocation();
    const isDelete = location?.state?.isDelete;
    const availableQty = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.availableQty`
    });

    const currentItemId = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.itemId`
    });

    const quantity = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.quantity`
    });

    const rate = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.rate`
    });

    const gst = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.gst`
    });

    const month = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.month`
    });

    const siteBudgetItemId = useWatch({
        control,
        name: `sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.siteBudgetItemId`
    });

    // Calculate Amount
    const amount = useMemo(() => {
        const qtyVal = parseFloat(quantity) || 0;
        const rateVal = parseFloat(rate) || 0;
        const gstVal = parseFloat(gst) || 0;
        const monthVal = parseFloat(month) || 0;

        let total = qtyVal * rateVal;

        if (isStaffBudget || isServiceRequest) {
            if (!isOldData) {
                total = total * monthVal;
            }
        }

        if (!isStaffBudget) {
            total = total + (total * gstVal / 100);
        }
        return total.toFixed(2);
    }, [quantity, rate, gst, month, isStaffBudget, isServiceRequest]);

    useEffect(() => {
        setValue(`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.amount`, formatAmount(amount));
    }, [amount, setValue, sectionIndex, headIndex, itemIndex]);

    // console.log("amount", isEditMode);

    return (
        <tr>
            <td>
                <select
                    className="form-select form-control"
                    value={currentItemId || ""}
                    disabled={isDelete}
                    {...register(`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.itemId`, {
                        required: `${itemLabel} is required`,
                        onChange: (e) => handleItemChange(itemIndex, e.target.value)
                    })}
                >
                    <option value="" hidden>Select {itemLabel}</option>
                    {
                        itemsList.length === 0 && (
                            <option value="" disabled>No {itemLabel} found</option>
                        )
                    }
                    {itemsList.map((opt) => {
                        const optValue = String(opt.itemId || opt.id);
                        const optLabel = opt?.item?.name || opt?.name;
                        return (
                            <option
                                key={optValue}
                                value={optValue}
                                disabled={
                                    (usedItems.map(String).includes(optValue) &&
                                        String(currentItemId) !== optValue) ||
                                    opt.alreadyUsed
                                }
                            >
                                {optLabel}
                            </option>
                        );
                    })}
                </select>
                <ErrorMessage
                    message={errors?.sections?.[sectionIndex]?.expenseHeads?.[headIndex]?.items?.[itemIndex]?.itemId?.message}
                />
            </td>
            {!isStaffBudget && (
                <td>
                    <input
                        type="text"
                        className="form-control"
                        readOnly
                        {...register(`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.hsnCode`)}
                        disabled
                    />
                </td>
            )}
            <td>
                <Controller
                    name={`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.quantity`}
                    control={control}
                    rules={isDelete ? {} : {
                        required: "Quantity is required",
                        validate: (value) => {
                            if (isEditMode && Number(value) < Number(availableQty || 0)) {
                                return `Minimum ${availableQty} required`;
                            }
                            return true;
                        }
                    }}
                    render={({ field }) => (
                        <div>
                            <input
                                type="text"
                                className="form-control"
                                {...field}
                                placeholder="Qty"
                                disabled={isDelete}
                                onChange={(e) => {
                                    let value = e.target.value
                                        .replace(/[^0-9.]/g, "")
                                        .replace(/(\..*)\./g, "$1");
                                    if (!/^\d*\.?\d*$/.test(value))
                                        value = "";
                                    field.onChange(value);
                                }}
                            />
                            {(isEditMode || location?.state?.data?.type === 2) && <small className="text-muted">
                                Min: {availableQty || 0}
                            </small>}
                        </div>
                    )}
                />
                <ErrorMessage
                    message={errors?.sections?.[sectionIndex]?.expenseHeads?.[headIndex]?.items?.[itemIndex]?.quantity?.message}
                />
            </td>
            {(isStaffBudget || isServiceRequest) && (
                <td>
                    <div>
                        <input
                            type="number"
                            className="form-control"
                            placeholder="Month"
                            onWheel={(e) => e.currentTarget.blur()}
                            {...register(`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.month`,
                                (isOldData && !isStaffBudget) || isDelete ? {} : {
                                    required: "Month is required",
                                    validate: (value) => {
                                        // console.log("Month validation:", { isEditMode, value, activeItemMonth: activeItem.month, comparison: Number(value) < Number(activeItem.month || 0) });
                                        if (isEditMode && Number(value) < Number(activeItem.month || 0)) {
                                            return `Minimum ${activeItem.month} required`;
                                        }
                                        return true;
                                    }
                                }
                            )}
                            disabled={isDelete || (isStaffBudget ? false : isOldData)}
                        />
                        {(isEditMode || location?.state?.data?.type === 2) && !isDelete && <small className="text-muted">
                            Min: {activeItem.month || 0}
                        </small>}
                    </div>
                    <ErrorMessage
                        message={errors?.sections?.[sectionIndex]?.expenseHeads?.[headIndex]?.items?.[itemIndex]?.month?.message}
                    />
                </td>
            )}
            <td>
                <Controller
                    name={`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.rate`}
                    control={control}
                    rules={isDelete ? {} : {
                        required: "Rate is required",
                        validate: (value) => {
                            if (isEditMode && Number(value) < Number(activeItem.rate || 0)) {
                                return `Minimum ${activeItem.rate} required`;
                            }
                            return true;
                        }
                    }}
                    render={({ field }) => (
                        <div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Rate"
                                {...field}
                                onChange={(e) => {
                                    let value = e.target.value
                                        .replace(/[^0-9.]/g, "")
                                        .replace(/(\..*)\./g, "$1");
                                    if (!/^\d*\.?\d*$/.test(value))
                                        value = "";
                                    field.onChange(value);
                                }}
                                disabled={isDelete}
                            />
                            {(isEditMode || location?.state?.data?.type === 2) && !isDelete && <small className="text-muted">
                                Min: {activeItem.rate || 0}
                            </small>}
                        </div>
                    )}
                />
                <ErrorMessage
                    message={errors?.sections?.[sectionIndex]?.expenseHeads?.[headIndex]?.items?.[itemIndex]?.rate?.message}
                />
            </td>
            {!isStaffBudget && (
                <td>
                    {isServiceRequest ? (
                        <>
                            <div>

                                <select
                                    className="form-select"
                                    {...register(`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.gst`,
                                        isOldData || isDelete ? {} : {
                                            required: "GST is required",
                                            validate: (value) => {
                                                if (isEditMode && Number(value) < Number(activeItem.gst || 0)) {
                                                    return `Minimum ${activeItem.gst}% required`;
                                                }
                                                return true;
                                            }
                                        }
                                    )}
                                    disabled={isDelete || isOldData}
                                >
                                    <option value="" hidden>GST %</option>
                                    {[0, 5, 12, 18, 28].map((rate) => (
                                        <option key={rate} value={rate}>
                                            {rate}%
                                        </option>
                                    ))}
                                </select>
                                {(isEditMode || location?.state?.data?.type === 2) && !isDelete && <small className="text-muted">
                                    Min: {activeItem.gst || 0}
                                </small>}
                            </div>
                            <ErrorMessage
                                message={errors?.sections?.[sectionIndex]?.expenseHeads?.[headIndex]?.items?.[itemIndex]?.gst?.message}
                            />
                        </>
                    ) : (
                        <>
                            <div>

                                <select
                                    className="form-select"
                                    {...register(`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.gst`,
                                        isOldData || isDelete ? {} : {
                                            required: "GST is required",
                                            validate: (value) => {
                                                if (isEditMode && Number(value) < Number(activeItem.gst || 0)) {
                                                    return `Minimum ${activeItem.gst}% required`;
                                                }
                                                return true;
                                            }
                                        }
                                    )}
                                    disabled={isDelete || isOldData}
                                >
                                    <option value="" hidden>GST %</option>
                                    {[0, 5, 12, 18, 28].map((rate) => (
                                        <option key={rate} value={rate}>
                                            {rate}%
                                        </option>
                                    ))}
                                </select>
                                {(isEditMode || location?.state?.data?.type === 2) && !isDelete && <small className="text-muted">
                                    Min: {activeItem.gst || 0}
                                </small>}
                            </div>
                            <ErrorMessage
                                message={errors?.sections?.[sectionIndex]?.expenseHeads?.[headIndex]?.items?.[itemIndex]?.gst?.message}
                            />
                        </>
                    )}
                </td>
            )}
            <td>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Amount"
                    readOnly
                    {...register(`sections.${sectionIndex}.expenseHeads.${headIndex}.items.${itemIndex}.amount`)}
                    disabled
                />
            </td>
            <td>
                <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => remove(itemIndex)}
                    disabled={isDelete}
                >
                    <DeleteIcon />
                </button>
            </td>
        </tr>
    );
};

export default BudgetRequestItemRow;

